import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { api, type TaskView } from '../../lib/client.ts';
import { tasksQuery } from '../records/record-sidebar.tsx';
import { AutomationBadge, runPending } from '../tasks/task-detail-dialog.tsx';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';

/**
 * The record's automated sends (pipeline automations that call a URL, e.g. the Marketplace
 * Reactivation emails and texts): what is still to go out and exactly when, then what went out,
 * was skipped or failed. Each send is an automated task due at its run's time (pipeline triggers),
 * so this reads the record's own task list: no extra endpoint, and the same data My Tasks shows.
 *
 * Times are New York's, the team's clock and the one the automations' days and hours use.
 * "Don't send" closes the automated task, which skips its run (the API's rule), for whoever may
 * change the task: its assignee, or anyone who can write to the record (`canWrite`).
 */
const WHEN = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
export const sendTime = (iso: string | null) => (iso ? `${WHEN.format(new Date(iso))} ET` : '—');

/** "in 2 days", "in 3 hours", "in 5 minutes", "now". */
export function fromNow(iso: string | null, now = Date.now()): string {
  if (!iso) return '';
  const minutes = Math.round((new Date(iso).getTime() - now) / 60_000);
  if (minutes <= 0) return 'now';
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} days`;
}

/** The automation's name (what the admin called it, e.g. "E7 – Day 7 email"), else the title. */
const label = (t: TaskView) => t.createdByAutomation?.name ?? t.title;

const RECENT = 5;

export function ScheduledMessages({
  entityType,
  entityId,
  canWrite,
}: {
  entityType: 'projects' | 'contacts';
  entityId: string;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const session = useSession();
  // As TaskRow: the task's assignee may close it even without write access to the record.
  const canCancel = (t: TaskView) => canWrite || t.assignee?.id === session.me.user.id;
  const tasks = useQuery({
    queryKey: tasksQuery(entityId, entityType),
    queryFn: () => api.tasks.list(entityType, entityId),
  });
  const [error, setError] = useState<string | null>(null);
  const cancel = useMutation({
    mutationFn: (id: string) => api.tasks.patch(id, { status: 'done' }),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e) => setError(describeError(e)),
  });

  if (tasks.isPending) return null;
  if (tasks.isError) {
    return (
      <Card className="p-4 text-sm text-destructive" role="alert">
        Could not load the scheduled messages: {describeError(tasks.error)}
      </Card>
    );
  }
  const automated = tasks.data.filter((t) => t.automated && t.automation);
  if (automated.length === 0) return null;
  const byDue = (a: TaskView, b: TaskView) => (a.dueAt ?? '').localeCompare(b.dueAt ?? '');
  const upcoming = automated.filter(runPending).sort(byDue);
  const done = automated
    .filter((t) => !runPending(t))
    .sort((a, b) => byDue(b, a))
    .slice(0, RECENT);

  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="scheduled-messages">
      <div>
        <h3 className="text-sm font-semibold">Automated messages</h3>
        <p className="text-xs text-muted-foreground">
          What the pipeline automations will send, at New York time. A send is skipped by itself
          when the lead moves to another stage before it is due.
        </p>
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <section aria-label="Scheduled" className="flex flex-col gap-1">
        <h4 className="text-xs font-medium uppercase text-muted-foreground">Scheduled</h4>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing scheduled.</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {upcoming.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium" title={label(t)}>
                  {label(t)}
                </span>
                <span className="text-sm tabular-nums" data-testid="send-time">
                  {sendTime(t.dueAt)}
                </span>
                <span className="text-xs text-muted-foreground">{fromNow(t.dueAt)}</span>
                <AutomationBadge task={t} />
                {canCancel(t) && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cancel.isPending}
                    onClick={() => {
                      if (window.confirm(`Don't send "${label(t)}"? It cannot be scheduled again.`))
                        cancel.mutate(t.id);
                    }}
                  >
                    Don't send
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {done.length > 0 && (
        <section aria-label="Recent" className="flex flex-col gap-1">
          <h4 className="text-xs font-medium uppercase text-muted-foreground">Recent</h4>
          <ul className="flex flex-col divide-y">
            {done.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="min-w-0 flex-1 truncate text-sm" title={label(t)}>
                  {label(t)}
                </span>
                <span className="text-sm tabular-nums text-muted-foreground">
                  {sendTime(t.automation?.processedAt ?? t.dueAt)}
                </span>
                <AutomationBadge task={t} />
                {t.automation?.state === 'skipped' && t.automation.error && (
                  <span className="w-full text-xs text-muted-foreground">{t.automation.error}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}
